"use client";

import ProductPage from "@/components/ProductPage";
import { Check } from "lucide-react";

import ImageHero from "./highlight.jpg";
import ImageHighlight from "./highlight.jpg";
import ImageFeaturePOS from "./feature-pos.jpg";
import ImageFeatureCustomers from "./feature-customers.jpg";
import ImageFeatureStock from "./feature-stock.jpg";
import ImageFeatureStats from "./feature-stats.jpg";
import ImageFeatureOrder from "./feature-order.jpg";
import ImageFeatureExport from "./feature-export.jpg";
import Section from "@/components/Section";
import SpecialFeaturesSection from "@/components/SpecialFeaturesSection";
import ImageText from "@/components/ImageText";
import Image from "next/image";

export default function Office4Trade() {
  return (
    <ProductPage
      productName="Office4Trade – Ihre Warenwirtschaft für den Handel"
      hero={{
        title: "Office4Trade – Ihre Warenwirtschaft für den Handel",
        subtitle:
          "Office4Trade bündelt alle zentralen Handelsprozesse in einer Lösung - von Angebot und Auftrag bis Auswertung und Export.",
        buttonLeft: {
          title: "Unverbindliche Anfrage",
          href: "/support",
        },
        buttonRight: { title: "Preise", href: "/preise" },
        image: ImageHero,
        imageAlt: "Office4Trade",
      }}
      details={{ paragraphs: [] }}
      highlight={{
        image: ImageHighlight,
        bullets: [
          <>
            <strong>Revisionssichere Archivierung</strong> inkl.{" "}
            <a
              href="https://www.hsp-steuer.de/2017/01/18/gobs-gobd-gdpdu-einfach-erklaert/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-primary underline hover:opacity-80"
            >
              GoBD
            </a>
            /
            <a
              href="https://www.hsp-steuer.de/2017/01/18/gobs-gobd-gdpdu-einfach-erklaert/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-primary underline hover:opacity-80"
            >
              GDPdU
            </a>
            -konform.
          </>,
          <>
            <a
              href="https://www.ris.bka.gv.at/GeltendeFassung.wxe?Abfrage=Bundesnormen&Gesetzesnummer=20009390"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-primary underline hover:opacity-80"
            >
              <strong>RKSV-konform</strong>
            </a>{" "}
            für Österreich.
          </>,
          <>
            <strong>Nahtlose Schnittstellen</strong> zu{" "}
            <a
              href="https://www.softguide.de/funktion/datev-export"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-primary underline hover:opacity-80"
            >
              DATEV
            </a>
            ,{" "}
            <a
              href="https://www.softguide.de/funktion/fibu-export"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-primary underline hover:opacity-80"
            >
              FIBU-Export
            </a>{" "}
            sowie Online-Verkaufssystemen.
          </>,
          <>
            <strong>Online-Verkauf</strong> – nahtlose Anbindung an Ihren
            Webshop.
          </>,
          <span>
            <strong>Finanzamt- und Gesetzkonform</strong>
          </span>,
        ],
      }}
      beforeVideo={
        <>
          <Section className="bg-background text-foreground py-12 md:py-20">
            <ImageText
              imagePosition="right"
              imageSlot={
                <div className="relative overflow-hidden mt-12 rounded-2xl border border-black/10 bg-black shadow-sm h-auto md:h-100">
                  <video
                    className="w-full h-auto md:h-full md:object-cover"
                    controls
                  >
                    <source src="/VideoFürWebseite.mp4" type="video/mp4" />
                  </video>
                </div>
              }
            >
              <div className="flex flex-col gap-2 mb-2">
                <Image
                  src="/officefortrade.svg"
                  alt="Office4Trade"
                  width={120}
                  height={36}
                  className="w-auto object-contain"
                />
                <h2 className="text-3xl md:text-4xl font-semibold text-[#050a18]">
                  Office4Trade – ERP- und Warenwirtschaftssystem für den Handel
                </h2>
              </div>

              <p className="text-slate-700 leading-relaxed">
                Office4Trade optimiert Ihre Abläufe im internationalen Handel:
                von der Auftrags- und Messeerfassung über Angebots- und
                Rechnungswesen bis hin zu Artikel- und Lieferantenmanagement.
              </p>

              <p className="mt-4 text-slate-700 leading-relaxed">
                Mit integrierten Statistiken, Datenimport/-export und
                Mehrfirmenverwaltung vereint Office4Trade alle wichtigen
                Funktionen in einer flexiblen Komplettlösung.
              </p>

              <ul className="mt-4 space-y-2">
                <li className="flex items-start gap-3 text-slate-700">
                  <Check
                    className="shrink-0 mt-1 text-brand-primary"
                    size={18}
                  />
                  <span>
                    <strong>Zentrale Verwaltung:</strong> Von Messeerfassung bis
                    Rechnungswesen.
                  </span>
                </li>
                <li className="flex items-start gap-3 text-slate-700">
                  <Check
                    className="shrink-0 mt-1 text-brand-primary"
                    size={18}
                  />
                  <span>
                    <strong>Flexibel & Skalierbar:</strong> Passt sich perfekt
                    Ihren Anforderungen an.
                  </span>
                </li>
                <li className="flex items-start gap-3 text-slate-700">
                  <Check
                    className="shrink-0 mt-1 text-brand-primary"
                    size={18}
                  />
                  <span>
                    <strong>Daten im Griff:</strong> Integrierte Statistiken und
                    einfacher Import/Export.
                  </span>
                </li>
              </ul>
            </ImageText>
          </Section>

          <SpecialFeaturesSection
            industries={[
              {
                title: "Messe & Eventverkauf",
                standard: [
                  "Kassieren",
                  "Artikelverwaltung",
                  "Zahlungsarten",
                  "Tagesabschluss",
                  "Belegdruck & E-Mail-Beleg",
                  "Rabatte & Aktionspreise",
                  "Statistiken & Umsatzübersicht",
                  "Mehrwertsteuer-Verwaltung",
                ],
                special: [
                  "Offlinefähig auch bei instabiler Internetverbindung",
                  "Optimierte Bedienung für Stoßzeiten",
                  "Mobiles Kassieren ohne festen Arbeitsplatz",
                  "Minimaler Setup-Aufwand direkt vor Ort",
                  "Schneller Standwechsel zwischen verschiedenen Messeorten",
                  "Mehrere Kassen parallel auf einem Event betreibbar",
                  "Übersichtliche Artikelkacheln für schnelles Tippen",
                  "Tagesabschluss mit exportierbarem Kassenbericht",
                ],
              },
            ]}
          />
        </>
      }
      faqs={[
        {
          q: "Ist Office4Trade auch für kleine Unternehmen geeignet?",
          a: "Ja. Office4Trade ist modular aufgebaut und kann sowohl in kleinen als auch in größeren Handelsunternehmen eingesetzt werden – Sie zahlen nur für das, was Sie wirklich brauchen.",
        },
        {
          q: "Unterstützt das System den Import von Artikeldaten?",
          a: "Ja, Artikel- und Kundendaten lassen sich bequem importieren und exportieren – unter anderem im CSV- und Excel-Format.",
        },
        {
          q: "Kann ich Statistiken für meine Verkäufe abrufen?",
          a: "Ja, Office4Trade liefert Ihnen aussagekräftige Statistiken und Auswertungen – von Tagesumsätzen über Artikelstatistiken bis hin zu Kundenanalysen.",
        },
        {
          q: "Wie erfolgt die Artikelerfassung?",
          a: "Ganz einfach per Scanner – schnell und fehlerfrei. Alternativ können Artikel auch manuell oder per Import angelegt werden.",
        },
        {
          q: "Lässt sich Office4Trade für mehrere Firmen nutzen?",
          a: "Ja, Sie können mehrere Firmen in einer Lösung verwalten – ideal für Unternehmen mit mehreren Standorten oder Filialen.",
        },
        {
          q: "Gibt es eine Anbindung an Buchhaltungssoftware?",
          a: "Ja, Office4Trade unterstützt den Export von Daten an gängige Buchhaltungssysteme wie DATEV – für eine nahtlose Übergabe ohne doppelte Datenpflege.",
        },
        {
          q: "Funktioniert Office4Trade auch offline?",
          a: "Ja, das System ist auch bei instabiler oder fehlender Internetverbindung einsatzbereit – besonders praktisch für den Einsatz auf Messen oder Events.",
        },
        {
          q: "Wie läuft die Einrichtung ab?",
          a: "Nach einer persönlichen Beratung richtet unser Team das System für Sie ein. Schulungen und Support sind im Einführungsprozess inbegriffen.",
        },
      ]}
      features={[
        {
          src: ImageFeaturePOS,
          alt: "Office4Trade - Angebote und Aufträge",
          title: "Angebote & Aufträge",
          desc: "Erstellen Sie Angebote, Aufträge, Auftragsbestätigungen und Proformarechnungen - alles in einem System.",
        },
        {
          src: ImageFeatureCustomers,
          alt: "Office4Trade - Lots und Artikelverwaltung",
          title: "Lots & Artikel",
          desc: "Weisen Sie ganze Lots zu und erfassen Sie Artikel einfach per Scanner - schnell und fehlerfrei.",
        },
        {
          src: ImageFeatureStats,
          alt: "Office4Trade - Statistiken und Auswertungen",
          title: "Statistiken",
          desc: "Erhalten Sie klare Statistiken für Verkäufe, Artikel und Kunden. Treffen Sie fundierte Entscheidungen.",
        },
        {
          src: ImageFeatureStock,
          alt: "Office4Trade - Kundenverwaltung",
          title: "Kundenverwaltung",
          desc: "Neukunden einfach anlegen und mit bestehenden Daten arbeiten. Alle Informationen stets griffbereit.",
        },
        {
          src: ImageFeatureOrder,
          alt: "Office4Trade - Liefertermine verwalten",
          title: "Liefertermine",
          desc: "Pflegen Sie Einzelliefertermine für Artikel und behalten Sie stets den Überblick über Bestellungen.",
        },
        {
          src: ImageFeatureExport,
          alt: "Office4Trade – Bilderkatalog und Multi-Firmen",
          title: "Bilderkatalog & Multi-Firmen",
          desc: "Verwalten Sie Artikel visuell im Bilderkatalog und nutzen Sie Office4Trade für mehrere Firmen.",
        },
        {
          src: ImageFeatureExport,
          alt: "Office4Trade – Import und Export",
          title: "Import & Export",
          desc: "Artikel- und Kundendaten lassen sich einfach importieren und exportieren – für maximale Flexibilität.",
        },
      ]}
    />
  );
}
